import * as React from "react";
import {
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  OverlayDrawer,
  Button,
  makeStyles,
  Field,
  Text,
  Input,
  Divider,
  Textarea,
  Switch,
  Checkbox,
  InfoLabel,
  Accordion,
  AccordionItem,
  AccordionHeader,
  AccordionPanel,
  Spinner,
  Dropdown
} from "@fluentui/react-components";
import { Dismiss24Regular, DismissCircle28Filled, BinRecycleFullFilled } from "@fluentui/react-icons";
import { useToolbarContext } from "../contexts/ToolbarContext";
import NewDropzone from "../room/newDropzone";
import { ARTWORK_TYPES, artworkFields, useUploadFormContext } from "../contexts/UploadFormContext";
import ArtworkDropdown from "../fluent/ArtworkDropdown";
import { FluentNumericInput } from "../fluent/NumericInputCompound";
import { ARTWORK_LEGAL_INFORMATION } from "../constants/artworks";
import useUrlUpload from "../hooks/useUrlUpload";
import { getMediaType } from "../../utils/custom-utils";
import useBreakpoints from "../hooks/useBreakpoints";
import { isThreeMeshUIValidInput, isYearValid } from "../../utils/three-mesh-ui-utils";
import { proxiedUrlFor } from "../../utils/media-url-utils";
import { useObjectSettings } from "../contexts/ObjectSettingsProvider";

const useStyles = makeStyles({
  headerDrawer: {
    display: " flex",
    padding: " var(--Font-size-600, 24px) 16px 12px var(--Font-size-600, 24px)",
    alignItems: " flex-start",
    gap: " 8px",
    alignSelf: " stretch",
    backgroundColor: "#16181E"
  },
  compoundNumericInput: {
    minWidth: "60px",
    backgroundColor: "rgba(245, 245, 245, 0.10)"
  },
  drawerBody: {
    paddingTop: "20px",
    paddingBottom: "40px"
  },
  search: {
    backgroundColor: "rgba(255, 255, 255, 0.10)",
    width: "100%"
  },
  section: {
    display: " flex",
    flexDirection: " column",
    alignItems: " flex-start",
    gap: "2px"
  },
  sectionHeader: {
    display: " flex",
    padding: " 8px 0px",
    flexDirection: " column",
    alignItems: " flex-start",
    gap: "var(--Font-size-100, 10px)",
    alignSelf: "stretch"
  },
  sectionHeaderText: {
    color: "var(--Gray-3, #828282)"
  },
  sectionContent: {
    width: "100%",
    display: " flex",
    padding: " 4px",
    alignItems: " center",
    height: "48px",
    borderRadius: "4px",
    justifyContent: "space-between",
    gap: " 8px",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.1)"
    }
  },
  persona: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "32px",
    "& .fui-Avatar__image": {
      width: "32px",
      height: "32px"
    },
    "& .fui-Avatar": {
      height: "32px"
    },
    "& .fui-Persona": {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "32px"
    }
  },
  icons: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    "& > *": {
      cursor: "pointer"
    }
  },
  divider: {
    padding: "5px 0px 4px 0px",
    marginTop: "20px",
    marginBottom: "20px"
  },
  divider2: {
    padding: "5px 0px 4px 0px",
    marginTop: "20px",
    marginBottom: "20px"
  },
  footer: {
    display: "flex",
    padding: "16px var(--Font-size-600, 24px) var(--Font-size-600, 24px) var(--Font-size-600, 24px)",
    justifyContent: "flex-end",
    width: "100%",
    backgroundColor: "#16181E"
  },
  redIcon: {
    color: "var(--Red, #EB5757)"
  },
  avatarGroupItem: {
    backgroundColor: "#333",
    "& img": {
      backgroundColor: "#333"
    }
  },
  avatarGroupItemAll: {
    "& img": {
      backgroundColor: "#333"
    }
  },
  textArtwork: {
    color: "var(--White, #FFF)",
    fontSize: "25px",
    fontWeight: "600"
  },
  input: {
    backgroundColor: "rgba(245, 245, 245, 0.1)",
    "& select": {
      backgroundColor: "rgba(245, 245, 245, 0.1)"
    },
    "& option": {
      backgroundColor: "rgba(245, 245, 245, 0.1)"
    }
  },
  thumbnail: {
    position: "relative",
    maxWidth: "100%",
    margin: "0 auto",
    "& .remove": {
      position: "absolute",
      top: "0",
      right: "0",
      cursor: "pointer",
      width: "30px",
      height: "30px",
      backgroundColor: "rgba(245, 245, 245, 0.3)",
      borderRadius: "50%",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: "3"
    }
  },
  accordionHeaderItem: {
    "& button": {
      paddingLeft: "0px"
    }
  }
});

export const FluentFileUpload = () => {
  const {
    mediums,
    setMediums,
    setFrames,
    formSetValue,
    onSubmitForm,
    handleSubmit,
    thumbnail,
    onChangeForm,
    handleFileLoadedDropzone,
    disabledSubmit,
    hasFile,
    onClose,
    loadingUpload,
    setThumbnail,
    url,
    setIndefiniteLoading,
    setErrorUpload
  } = useUploadFormContext();
  const { fileUploadOpen, onFileUploadOpen, isDigitalTwinArt, isDigitalArt } = useToolbarContext();

  const { errorUpload, urlUpload, onErrorUrlUploadChange } = useUrlUpload();

  React.useEffect(() => {
    if (fileUploadOpen) {
      onErrorUrlUploadChange(null);
    }
  }, [fileUploadOpen, onErrorUrlUploadChange]);

  const styles = useStyles();

  const optionMediums = mediums?.length
    ? mediums.map(medium => ({ value: medium.model, label: medium.name, ...medium }))
    : [];

  React.useEffect(() => {
    setMediums();
    setFrames();
  }, []);

  const { unit } = artworkFields;

  React.useEffect(() => {
    if (isDigitalTwinArt) {
      formSetValue(unit, "in");
    } else if (isDigitalArt) {
      formSetValue(unit, "custom");
    } else {
      formSetValue(unit, "");
    }

    formSetValue(artworkFields.type, isDigitalTwinArt ? ARTWORK_TYPES.DIGITAL_TWIN_ART : ARTWORK_TYPES.DIGITAL_ART);
  }, [isDigitalTwinArt, isDigitalArt, formSetValue, unit]);

  const buttonIcon = loadingUpload ? <Spinner size={"tiny"} /> : undefined;

  const onClearFile = () => {
    formSetValue(artworkFields.file, null);
    setThumbnail(null);
    onErrorUrlUploadChange(null);
  };

  const onUrlUpload = async () => {
    setIndefiniteLoading(true);
    setErrorUpload(false);
    onErrorUrlUploadChange(null);
    try {
      const uploadData = await urlUpload(url);
      if (uploadData) {
        const newFile = [
          {
            downloableUrl: uploadData.url,
            uploadData
          }
        ];

        formSetValue(artworkFields.file, newFile);
        setThumbnail(uploadData.thumbnail);
        formSetValue(artworkFields.myMediaId, uploadData.id);
        onErrorUrlUploadChange(null);
      }
      setIndefiniteLoading(false);
    } catch (error) {
      setIndefiniteLoading(false);
    }
  };

  const breakpoint = useBreakpoints();
  const isMobile = breakpoint === "sm" || breakpoint === "xs";

  const overlayStyle = isMobile ? {} : { width: "448px" };

  return (
    <OverlayDrawer
      size="full"
      style={overlayStyle}
      modalType="non-modal"
      position="end"
      open={fileUploadOpen}
      onOpenChange={(_, { open }) => onFileUploadOpen(open)}
      as="form"
      onSubmit={handleSubmit(onSubmitForm)}
    >
      <DrawerHeader className={styles.headerDrawer}>
        <DrawerHeaderTitle
          style={{ width: "100%" }}
          action={
            <Button appearance="subtle" aria-label="Close" icon={<Dismiss24Regular />} onClick={() => onClose()} />
          }
        >
          Upload
        </DrawerHeaderTitle>
      </DrawerHeader>
      {/* <form as="form" onSubmit={onSubmitForm}> */}
      <DrawerBody className={styles.drawerBody} id="upload-object-modal">
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          {!thumbnail && <NewDropzone thumbnail={thumbnail} onFileLoaded={handleFileLoadedDropzone}></NewDropzone>}
          {thumbnail && <ThumbnailComponent onClear={onClearFile} styles={styles} thumbnail={thumbnail} />}

          {!hasFile && (
            <>
              <Text color="#fff" style={{ textTransform: "uppercase" }} align="center">
                {" "}
                OR{" "}
              </Text>
              <Field
                validationMessage={errorUpload ? errorUpload : undefined}
                label={{
                  children: (_, props) => (
                    <InfoLabel {...props} info="Upload files via URL">
                      URL
                    </InfoLabel>
                  )
                }}
              >
                <Input
                  type="text"
                  placeholder="Add URL"
                  onChange={e => {
                    onChangeForm(e, artworkFields.url);
                  }}
                  appearance="filled-lighter"
                  className={styles.input}
                  onBlur={e => onUrlUpload()}
                  onKeyDown={e => {
                    if (e.key === "Enter") {
                      onUrlUpload();
                    }
                  }}
                />
              </Field>
            </>
          )}
        </div>

        {(isDigitalArt || isDigitalTwinArt) && (
          <>
            <Divider className={styles.divider} />
            <WorkInformation mediums={optionMediums} styles={styles} />
            <Divider className={styles.divider2} />
            <ArtistInformation styles={styles} />
            <Divider className={styles.divider2} />
            <LegalInformation styles={styles} />
            <Divider className={styles.divider2} />
            <AICreationIndification styles={styles} />
            <Divider className={styles.divider2} />
            <Checkbox
              onChange={e => onChangeForm(e, artworkFields.terms, "toggle")}
              label={"I accept the terms and conditions"}
              required
            />
          </>
        )}

        {!isDigitalArt && !isDigitalTwinArt && (
          <>
            <Divider className={styles.divider} />
            <GeneralTitleDescription styles={styles} />
            <Divider className={styles.divider} />
            <Accordion collapsible multiple>
              <AccordionItem value="1">
                <AccordionHeader className={styles.accordionHeaderItem}>
                  <Text size={200} style={{ lineHeight: "20px" }}>
                    Advanced Options
                  </Text>
                </AccordionHeader>
                <AccordionPanel style={{ display: "flex", flexDirection: "column", gap: "20px", margin: "0" }}>
                  <Field
                    label={{
                      children: (_, props) => (
                        <InfoLabel {...props} info="Redirect users to another location">
                          URL
                        </InfoLabel>
                      )
                    }}
                  >
                    <Input
                      onChange={e => onChangeForm(e, "href")}
                      type="text"
                      placeholder="Add URL"
                      appearance="filled-lighter"
                      className={styles.input}
                    />
                  </Field>

                  <Field
                    label={{
                      children: (_, props) => (
                        <InfoLabel {...props} info="Use a button on the object to navigate to the URL.">
                          Hide Open Link Button
                        </InfoLabel>
                      )
                    }}
                  >
                    <Switch
                      label={`Hide Open Link button for the Custom URL and make the object fully clickable`}
                      labelPosition="before"
                      onChange={e => onChangeForm(e, "hideHref", "toggle")}
                    />
                  </Field>
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          </>
        )}
      </DrawerBody>
      <div className={styles.footer} style={{ justifyContent: "flex-end" }}>
        <Button icon={buttonIcon} appearance="primary" type="submit" disabled={disabledSubmit}>
          Upload
        </Button>
      </div>
      {/* </form> */}
    </OverlayDrawer>
  );
};

function ThumbnailComponent({ thumbnail, onClear, styles }) {
  const mediaType = getMediaType(thumbnail);

  const exceptions = ["audio", "video"];
  return (
    <div className={styles.thumbnail}>
      <div className="remove" onClick={onClear}>
        <BinRecycleFullFilled style={{ width: "20px", height: "20px" }} />
      </div>
      {mediaType === "audio" && <audio style={{ padding: "4px", marginRight: "23px" }} src={thumbnail} controls />}
      {mediaType === "video" && <video src={proxiedUrlFor(thumbnail)} controls />}
      {!exceptions.includes(mediaType) && <img src={thumbnail}></img>}
    </div>
  );
}

function GeneralTitleDescription({ styles }) {
  const { onChangeForm } = useUploadFormContext();
  const [titleError, setTitleError] = React.useState(false);
  const [descriptionError, setDescriptionError] = React.useState(false);
  const [labelsError, setLabelsError] = React.useState(false);

  const handleTitleChange = e => {
    const value = e.target.value;
    onChangeForm(e, artworkFields.title);
    setTitleError(!isThreeMeshUIValidInput(value));
  };

  const handleDescriptionChange = e => {
    const value = e.target.value;
    onChangeForm(e, "description");
    setDescriptionError(!isThreeMeshUIValidInput(value));
  };

  const handleLabelsChange = e => {
    const value = e.target.value;
    onChangeForm(e, "labels");
    setLabelsError(!isThreeMeshUIValidInput(value));
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
      <Field
        label={"Title"}
        required
        validationMessage={titleError ? "Some characters in the title are not valid" : undefined}
      >
        <Input
          appearance="filled-lighter"
          className={styles.input}
          type="text"
          placeholder="Add name"
          onChange={handleTitleChange}
        />
      </Field>
      <Field
        label={"Description"}
        validationMessage={descriptionError ? "Some characters in the description are not valid" : undefined}
      >
        <Textarea
          rows={3}
          appearance="filled-lighter"
          className={styles.input}
          type="text"
          placeholder="Descriptive detail of the work..."
          onChange={handleDescriptionChange}
        />
      </Field>

      <Field
        validationMessage={labelsError ? "Some characters in the labels are not valid" : undefined}
        label={{
          children: (_, props) => (
            <InfoLabel {...props} info="Use labels to categorize and organize content for easier identification">
              Labels
            </InfoLabel>
          )
        }}
      >
        <Input
          appearance="filled-lighter"
          className={styles.input}
          type="text"
          placeholder="Add labels"
          onChange={handleLabelsChange}
        />
      </Field>

      {/* <Field>
        <Switch
          onChange={e => onChangeForm(e, artworkFields.enableARTrigger, "toggle")}
          label={{
            children: (_, props) => (
              <InfoLabel
                {...props}
                info="Lorem ipsum dolor sit amet consectetur. Consectetur quis egestas et maecenas nunc nulla viverra in."
              >
                Enable AR Trigger
              </InfoLabel>
            )
          }}
        />
      </Field> */}
    </div>
  );
}

function WorkInformation({ styles, mediums }) {
  const { isDigitalTwinArt, isDigitalArt } = useToolbarContext();
  const title = isDigitalTwinArt ? "Digital Twin Art" : "Digital Art";
  const infoLabelText = isDigitalTwinArt
    ? "Create a digital twin of your physical artwork. Provide details about your physical piece to create a digital art label and a QR code, allowing users to view a digital version of your work."
    : "Upload your digital artwork. This option generates an art label and a QR code, allowing you to share your digital pieces with the community.";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      <InfoLabel size="large" info={infoLabelText}>
        <Text className={styles.textArtwork}>{title} </Text>
      </InfoLabel>
      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        <Text weight="semibold" style={{ fontSize: "18px" }}>
          Work Information
        </Text>
        <WorkInformationContent
          styles={styles}
          mediums={mediums}
          isDigitalArt={isDigitalArt}
          isDigitalTwinArt={isDigitalTwinArt}
        />
      </div>
    </div>
  );
}

export function WorkInformationContent({
  styles,
  mediums,
  isDigitalTwinArt,
  isDigitalArt,
  defaultValues = {},
  disabled = false,
  medium,
  isPublicDomainArt
}) {
  const { digitalizationSelect, currentStateSelect, onChangeForm, frames, file, url } = useUploadFormContext();
  const { selectedObject } = useObjectSettings();

  const optionFrames = frames?.length ? frames.map(frame => ({ value: frame.model, label: frame.name, ...frame })) : [];

  const [enableARTrigger, setEnableARTrigger] = React.useState(defaultValues[artworkFields.enableARTrigger] || false);

  const handleSwitchChange = e => {
    setEnableARTrigger(e.target.checked);
    onChangeForm(e, "enableARTrigger", "toggle");
  };

  const [selectedMedium, setSelectedMedium] = React.useState({ label: defaultValues[artworkFields.medium] || "--" });
  const [selectedFrame, setSelectedFrame] = React.useState({ label: defaultValues[artworkFields.frame] || "--" });
  const [selectedTechnique, setSelectedTechnique] = React.useState({
    label: defaultValues[artworkFields.techniqueUsed] || "--"
  });
  const [selectedCurrentState, setSelectedCurrentState] = React.useState({
    label: defaultValues[artworkFields.currentPhysicalWork] || "--"
  });

  const handleMediumChange = (value, option) => {
    setSelectedMedium(option);
    onChangeForm(option, "medium", "value", option);
  };

  const handleFrameChange = (value, option) => {
    setSelectedFrame(option);
    onChangeForm(option, artworkFields.frame, "value", option);
  };

  const handleTechniqueChange = (value, option) => {
    setSelectedTechnique(option);
    onChangeForm(option, artworkFields.techniqueUsed, "value", value);
  };

  const handleCurrentStateChange = (value, option) => {
    setSelectedCurrentState(option);
    onChangeForm(option, artworkFields.currentPhysicalWork, "value", value);
  };

  const [titleError, setTitleError] = React.useState(false);
  const [descriptionError, setDescriptionError] = React.useState(false);
  const [labelsError, setLabelsError] = React.useState(false);
  const [dateError, setDateError] = React.useState(false);
  const [customMediumError, setCustomMediumError] = React.useState(false);

  const handleTitleChange = e => {
    const value = e.target.value;
    onChangeForm(e, artworkFields.artName);
    setTitleError(!isThreeMeshUIValidInput(value));
  };

  const handleDescriptionChange = e => {
    const value = e.target.value;
    onChangeForm(e, "description");
    setDescriptionError(!isThreeMeshUIValidInput(value));
  };

  const handleLabelsChange = e => {
    const value = e.target.value;
    onChangeForm(e, "labels");
    setLabelsError(!isThreeMeshUIValidInput(value));
  };

  const handleDateChange = e => {
    const value = e.target.value;
    onChangeForm(e, artworkFields.artworkDate);
    setDateError(!isYearValid(value));
  };

  React.useEffect(() => {
    if (defaultValues[artworkFields.artworkDate]) {
      const dateError = !isYearValid(defaultValues[artworkFields.artworkDate]);
      setDateError(dateError);
    }
  }, [artworkFields.artworkDate]);

  const handleCustomMediumChange = e => {
    const value = e.target.value;
    onChangeForm(e, artworkFields.customMedium);
    setCustomMediumError(!isThreeMeshUIValidInput(value));
  };

  const mediaType = selectedObject
    ? selectedObject?.el?.components['gltf-model-plus']?.model?.userData?.metadata?.imageTexture
      ? "image"
      : getMediaType(selectedObject?.el?.components["media-loader"]?.data?.src)
    : file && file.length
    ? file[0] instanceof File
      ? getMediaType(file[0].downloableUrl)
      : getMediaType(url)
    : null;

  React.useEffect(() => {}, []);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
      <Field
        label={"Title"}
        required
        validationMessage={titleError ? "Some characters in the title are not valid" : undefined}
      >
        <Input
          disabled={disabled || isPublicDomainArt}
          defaultValue={defaultValues[artworkFields.artName]}
          appearance="filled-lighter"
          className={styles.input}
          type="text"
          placeholder="Add name"
          onChange={handleTitleChange}
        />
      </Field>
      <Field
        label={"Description"}
        validationMessage={descriptionError ? "Some characters in the description are not valid" : undefined}
      >
        <Textarea
          disabled={disabled || isPublicDomainArt}
          defaultValue={medium?.label || defaultValues[artworkFields.description]}
          rows={3}
          appearance="filled-lighter"
          className={styles.input}
          type="text"
          placeholder="Descriptive detail of the work..."
          onChange={handleDescriptionChange}
        />
      </Field>
      <Field label={"Date"} required validationMessage={dateError ? "input must be a valid Year" : undefined}>
        <Input
          disabled={disabled || isPublicDomainArt}
          defaultValue={defaultValues[artworkFields.artworkDate]}
          appearance="filled-lighter"
          className={styles.input}
          placeholder="YYYY"
          onChange={handleDateChange}
        />
      </Field>
      <>
        <Field label={"Medium"}>
          <ArtworkDropdown
            id={"medium"}
            searchByLabel={true}
            disabled={disabled}
            value={selectedMedium.label}
            options={mediums}
            className={styles.input}
            placeholder="Select a Medium"
            onChange={handleMediumChange}
          />
        </Field>

        {selectedMedium.label === "Custom" && (
          <Field
            label={"Custom Medium"}
            validationMessage={customMediumError ? "Medium has an invalid character" : undefined}
          >
            <Input
              searchByLabel={true}
              appearance="filled-lighter"
              id={"custom Medium"}
              disabled={disabled}
              defaultValue={defaultValues[artworkFields.customMedium]}
              className={styles.input}
              placeholder="Custom Medium"
              onChange={handleCustomMediumChange}
            />
          </Field>
        )}

        {mediaType && mediaType === "image" && (
          <Field label={"Frame"}>
            <ArtworkDropdown
              id={"frame"}
              value={selectedFrame.label}
              disabled={disabled}
              options={optionFrames}
              className={styles.input}
              placeholder="Select a Frame"
              onChange={handleFrameChange}
            />
          </Field>
        )}
      </>
      {isDigitalTwinArt && (
        <Field
          label={{
            children: (_, props) => (
              <InfoLabel
                {...props}
                info="Artwork dimensions in inches typically refer to the physical size of the piece"
              >
                Dimensions
              </InfoLabel>
            )
          }}
        >
          <div style={{ display: "flex", gap: "10px" }}>
            <FluentNumericInput
              defaultValue={defaultValues[artworkFields.dimensionsY]}
              disabled={disabled || isPublicDomainArt}
              min={0}
              placeholder={"Height (in)"}
              className={styles.compoundNumericInput}
              onChange={value => onChangeForm({}, "dimensionsY", "value", value)}
            />
            <FluentNumericInput
              defaultValue={defaultValues[artworkFields.dimensionsX]}
              min={0}
              disabled={disabled || isPublicDomainArt}
              placeholder={"Width (in)"}
              className={styles.compoundNumericInput}
              onChange={value => onChangeForm({}, "dimensionsX", "value", value)}
            />
            <FluentNumericInput
              defaultValue={defaultValues[artworkFields.dimensionsZ]}
              min={0}
              disabled={disabled || isPublicDomainArt}
              placeholder={"Depth (in)"}
              className={styles.compoundNumericInput}
              onChange={value => onChangeForm({}, "dimensionsZ", "value", value)}
            />
          </div>
        </Field>
      )}
      {isDigitalTwinArt && !isDigitalArt && (
        <>
          <Field
            label={{
              children: (_, props) => (
                <InfoLabel {...props} info="Method used to digitalize the artwork">
                  Digitization technique used
                </InfoLabel>
              )
            }}
          >
            <ArtworkDropdown
              id="technique"
              value={selectedTechnique.label}
              disabled={disabled || isPublicDomainArt}
              options={digitalizationSelect}
              className={styles.input}
              type="text"
              placeholder="Select the technique"
              onChange={handleTechniqueChange}
            />
          </Field>
          <Field
            label={{
              children: (_, props) => (
                <InfoLabel
                  {...props}
                  info="Select the current condition of the physical artwork from the dropdown menu."
                >
                  Current state of the physical work
                </InfoLabel>
              )
            }}
          >
            <ArtworkDropdown
              value={selectedCurrentState.label}
              disabled={disabled || isPublicDomainArt}
              options={currentStateSelect}
              className={styles.input}
              type="text"
              placeholder="Select status"
              onChange={handleCurrentStateChange}
            />
          </Field>
        </>
      )}

      <Field
        validationMessage={labelsError ? "Some characters in the labels are not valid" : undefined}
        label={{
          children: (_, props) => (
            <InfoLabel {...props} info="Use this field to add labels or tags that categorize or describe the artwork.">
              Labels
            </InfoLabel>
          )
        }}
      >
        <Input
          disabled={disabled || isPublicDomainArt}
          defaultValue={defaultValues[artworkFields.labels]}
          appearance="filled-lighter"
          className={styles.input}
          type="text"
          placeholder="Add labels"
          onChange={handleLabelsChange}
        />
      </Field>

      {/* <Field>
            <Switch
              checked={enableARTrigger}
              // defaultValue={defaultValues[artworkFields.enableARTrigger]}
              onChange={e => handleSwitchChange(e)}
              label={{
                children: (_, props) => (
                  <InfoLabel
                    {...props}
                    info="Lorem ipsum dolor sit amet consectetur. Consectetur quis egestas et maecenas nunc nulla viverra in."
                  >
                    Enable AR Trigger
                  </InfoLabel>
                )
              }}
            />
          </Field> */}
    </div>
  );
}

function ArtistInformation({ styles }) {
  const { isDigitalTwinArt } = useToolbarContext();

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        <Text weight="semibold" style={{ fontSize: "18px" }}>
          Artist Information
        </Text>
        <ArtistInformationContent styles={styles} isDigitalTwinArt={isDigitalTwinArt} />
      </div>
    </div>
  );
}

export function ArtistInformationContent({ styles, isDigitalTwinArt, defaultValues = {}, disabled = false }) {
  const { onChangeForm } = useUploadFormContext();
  const [artistError, setArtistError] = React.useState(false);
  const [bioError, setBioError] = React.useState(false);
  const [statementError, setStatementError] = React.useState(false);

  const handleArtistChange = e => {
    const value = e.target.value;
    onChangeForm(e, artworkFields.artistName);
    setArtistError(!isThreeMeshUIValidInput(value));
  };

  const handleBioChange = e => {
    const value = e.target.value;
    onChangeForm(e, artworkFields.artistBio);
    setBioError(!isThreeMeshUIValidInput(value));
  };

  const handleStatementChange = e => {
    const value = e.target.value;
    onChangeForm(e, artworkFields.artistStatement);
    setStatementError(!isThreeMeshUIValidInput(value));
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
      <Field
        label={"Name"}
        required
        validationMessage={artistError ? "Some characters in the artist name are not valid" : undefined}
      >
        <Input
          disabled={disabled}
          defaultValue={defaultValues[artworkFields.artistName]}
          appearance="filled-lighter"
          className={styles.input}
          type="text"
          placeholder="Artist Name"
          onChange={handleArtistChange}
        />
      </Field>
      <Field label={"Bio"} validationMessage={bioError ? "Some characters in the bio are not valid" : undefined}>
        <Textarea
          disabled={disabled}
          defaultValue={defaultValues[artworkFields.artistBio]}
          rows={3}
          appearance="filled-lighter"
          className={styles.input}
          type="text"
          placeholder="Brief biography of the artist..."
          onChange={handleBioChange}
        />
      </Field>
      <Field
        label={"Statement"}
        validationMessage={statementError ? "Some characters in the statement are not valid" : undefined}
      >
        <Textarea
          disabled={disabled}
          defaultValue={defaultValues[artworkFields.artistStatement]}
          rows={3}
          appearance="filled-lighter"
          className={styles.input}
          type="text"
          placeholder="Artist statement about his work, aspirations and goals..."
          onChange={handleStatementChange}
        />
      </Field>
      {isDigitalTwinArt && (
        <Field label={"Date"}>
          <Input
            disabled={disabled}
            defaultValue={defaultValues[artworkFields.authorDate]}
            appearance="filled-lighter"
            className={styles.input}
            type="date"
            placeholder="DD-MM-YYYY"
            onChange={e => onChangeForm(e, artworkFields.authorDate)}
          />
        </Field>
      )}
    </div>
  );
}

function LegalInformation({ styles }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        <Text weight="semibold" style={{ fontSize: "18px" }}>
          Legal and Rights Information
        </Text>
        <LegalInformationContent styles={styles} />
      </div>
    </div>
  );
}

export function LegalInformationContent({ styles, defaultValues = {}, disabled = false }) {
  const legalOptions = ARTWORK_LEGAL_INFORMATION.map(legalInfo => ({ value: legalInfo, label: legalInfo }));
  const { onChangeForm } = useUploadFormContext();
  const [selectedLicense, setSelectedLicense] = React.useState({ label: defaultValues[artworkFields.license] || "--" });
  const handleLicenseChange = (value, option) => {
    setSelectedLicense(option);
    onChangeForm(option, artworkFields.license, "value", value);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
      <Field
        label={{
          children: (_, props) => (
            <InfoLabel {...props} info="Select the appropriate license for the artwork from the dropdown menu">
              Use License
            </InfoLabel>
          )
        }}
        required
      >
        <ArtworkDropdown
          disabled={disabled}
          value={selectedLicense.label}
          onChange={handleLicenseChange}
          options={legalOptions}
          className={styles.input}
          type="text"
          placeholder="Select the type of license"
        />
      </Field>
      <Checkbox
        disabled={disabled}
        defaultChecked={defaultValues[artworkFields.declareRights]}
        defaultValue={defaultValues[artworkFields.declareRights]}
        onChange={e => onChangeForm(e, artworkFields.declareRights, "toggle")}
        label={"I declare that this work is original and I have the necessary rights for its publication."}
        required
      />
    </div>
  );
}

function AICreationIndification({ styles }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        <Text weight="semibold" style={{ fontSize: "18px" }}>
          AI Creation Indication
        </Text>
        <AICreationIndicationContent styles={styles} />
      </div>
    </div>
  );
}

export function AICreationIndicationContent({ styles, defaultValues = {}, disabled = false }) {
  const { onChangeForm } = useUploadFormContext();

  const [toolError, setToolError] = React.useState(false);
  const [aiCreationChecked, setAICreationChecked] = React.useState(defaultValues[artworkFields.AIcreation] || false);

  const handleToolChange = e => {
    const value = e.target.value;
    onChangeForm(e, artworkFields.aiToolUsed);
    setToolError(!isThreeMeshUIValidInput(value)); // Validate input
  };

  const handleCheckboxChange = e => {
    const checked = e.target.checked;
    setAICreationChecked(checked);
    onChangeForm(e, artworkFields.AIcreation, "toggle");
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
      <Checkbox
        disabled={disabled}
        defaultChecked={aiCreationChecked}
        label={"This work was created in whole or in part by an AI"}
        onChange={e => handleCheckboxChange(e)}
      />
      <Field
        label={"What AI Tool Used?"}
        validationMessage={toolError ? "Some characters in the tool name are not valid" : undefined}
      >
        <Input
          disabled={disabled || !aiCreationChecked}
          defaultValue={defaultValues[artworkFields.aiToolUsed]}
          appearance="filled-lighter"
          className={styles.input}
          type="text"
          placeholder="Tool name"
          onChange={handleToolChange}
        />
      </Field>
    </div>
  );
}
